import React, { useState } from 'react';
import { Card, Input, Button } from '../../components/ui';
import { AlertTriangle, TrendingUp, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

export function HoaVendorBidComparerTool() {
  const [vendor1, setVendor1] = useState('Vendor A');
  const [v1Cost, setV1Cost] = useState('45000');
  const [v1Time, setV1Time] = useState('4');
  const [v1Warranty, setV1Warranty] = useState('5');
  
  const [vendor2, setVendor2] = useState('Vendor B');
  const [v2Cost, setV2Cost] = useState('52000');
  const [v2Time, setV2Time] = useState('3');
  const [v2Warranty, setV2Warranty] = useState('10');

  // Basic normalization scoring approach (higher is better)
  const c1 = parseFloat(v1Cost) || 0;
  const c2 = parseFloat(v2Cost) || 0;
  const minCost = Math.min(c1, c2) || 1;
  const scoreC1 = (minCost / (c1 || 1)) * 40; // 40% weight to cost
  const scoreC2 = (minCost / (c2 || 1)) * 40;

  const t1 = parseFloat(v1Time) || 0;
  const t2 = parseFloat(v2Time) || 0;
  const minTime = Math.min(t1, t2) || 1;
  const scoreT1 = (minTime / (t1 || 1)) * 30; // 30% weight to time
  const scoreT2 = (minTime / (t2 || 1)) * 30;

  const w1 = parseFloat(v1Warranty) || 0;
  const w2 = parseFloat(v2Warranty) || 0;
  const maxW = Math.max(w1, w2) || 1;
  const scoreW1 = (w1 / maxW) * 30; // 30% weight to warranty
  const scoreW2 = (w2 / maxW) * 30;

  const total1 = scoreC1 + scoreT1 + scoreW1;
  const total2 = scoreC2 + scoreT2 + scoreW2;

  const v1Wins = total1 > total2;
  const tie = total1 === total2;

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-bold">Proposal 1</h3>
          <Input placeholder="Vendor Name" value={vendor1} onChange={e => setVendor1(e.target.value)} className="mb-2 font-bold" />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Total Cost ($)</label>
              <Input type="number" value={v1Cost} onChange={e => setV1Cost(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Weeks to Complete</label>
              <Input type="number" value={v1Time} onChange={e => setV1Time(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Warranty (Yrs)</label>
              <Input type="number" value={v1Warranty} onChange={e => setV1Warranty(e.target.value)} />
            </div>
          </div>
        </Card>
        
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-bold">Proposal 2</h3>
          <Input placeholder="Vendor Name" value={vendor2} onChange={e => setVendor2(e.target.value)} className="mb-2 font-bold" />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Total Cost ($)</label>
              <Input type="number" value={v2Cost} onChange={e => setV2Cost(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Weeks to Complete</label>
              <Input type="number" value={v2Time} onChange={e => setV2Time(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Warranty (Yrs)</label>
              <Input type="number" value={v2Warranty} onChange={e => setV2Warranty(e.target.value)} />
            </div>
          </div>
        </Card>
      </div>

      <div className="space-y-6 flex flex-col justify-center">
        <Card className="p-6 bg-slate-50 border-slate-200 dark:bg-slate-900/20 h-full flex flex-col justify-center relative overflow-hidden">
            <span className="text-[10px] uppercase font-bold block mb-4 tracking-widest text-slate-500">Value Matrix Recommendation</span>
            
            <div className="space-y-6 z-10 relative">
               <div className={`p-4 rounded-xl border-2 transition-all ${v1Wins && !tie ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/40' : 'border-transparent bg-white dark:bg-slate-800 opacity-60'}`}>
                 <div className="flex justify-between items-center mb-2">
                   <h4 className="font-black text-lg">{vendor1 || 'Vendor 1'}</h4>
                   <span className="font-mono font-bold text-xl">{total1.toFixed(1)} <span className="text-xs text-gray-400">pts</span></span>
                 </div>
                 <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden dark:bg-slate-700">
                    <div className="bg-blue-500 h-full" style={{ width: `${total1}%` }}></div>
                 </div>
               </div>
               
               <div className={`p-4 rounded-xl border-2 transition-all ${!v1Wins && !tie ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/40' : 'border-transparent bg-white dark:bg-slate-800 opacity-60'}`}>
                 <div className="flex justify-between items-center mb-2">
                   <h4 className="font-black text-lg">{vendor2 || 'Vendor 2'}</h4>
                   <span className="font-mono font-bold text-xl">{total2.toFixed(1)} <span className="text-xs text-gray-400">pts</span></span>
                 </div>
                 <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden dark:bg-slate-700">
                    <div className="bg-purple-500 h-full" style={{ width: `${total2}%` }}></div>
                 </div>
               </div>
            </div>
            
            <p className="text-xs text-slate-500 mt-6 z-10">Scoring formula weighs Cost (40%), Timeline (30%), and Warranty Coverage (30%) relative to the most competitive offer in each category.</p>
        </Card>
      </div>
    </div>
  );
}

